import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, LogOut, Shield, Copy, Plus } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../components/Avatar";
import { InstallEntry } from "../components/InstallBanner";
import { PushToggle } from "../components/PushToggle";
import { RingSoundToggle } from "../components/RingSoundToggle";
import { ru } from "../locales/ru";

const inputCls = "w-full h-12 px-4 bg-gray-100 rounded-xl text-base outline-none focus:ring-2 focus:ring-black";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", car_model: "", car_color: "", car_plate: "" });
  const [invites, setInvites] = useState([]);
  const [saving, setSaving] = useState(false);

  const isDriver = user?.roles?.includes("driver");
  const isAdmin = user?.roles?.includes("admin");

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        car_model: user.car?.model || "",
        car_color: user.car?.color || "",
        car_plate: user.car?.plate || "",
      });
      api.get("/invites").then(({ data }) => setInvites(data)).catch(() => {});
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/me", form);
      setUser(data);
      toast.success(ru.profile.saved);
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/me/avatar", fd);
      setUser(data);
      toast.success(ru.profile.avatarUpdated);
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    }
    e.target.value = "";
  };

  const createInvite = async () => {
    try {
      const { data } = await api.post("/invites", { max_uses: 1 });
      setInvites([data, ...invites]);
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success(ru.invites.copied);
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!user) return null;

  return (
    <div className="px-6 pt-8 pb-28" data-testid="profile-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-extrabold tracking-tight">{ru.profile.title}</h1>
        <button data-testid="logout-button" onClick={doLogout} className="p-2.5 rounded-full bg-gray-100 active:scale-[0.95] transition-transform">
          <LogOut size={20} />
        </button>
      </div>

      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => fileRef.current?.click()} data-testid="avatar-upload-button" className="relative">
          <Avatar avatar={user.avatar} name={user.name} size={72} testId="profile-avatar" />
          <span className="absolute -bottom-1 -right-1 bg-black text-white rounded-full p-1.5">
            <Camera size={13} />
          </span>
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadAvatar} className="hidden" data-testid="avatar-file-input" />
        <div>
          <p className="font-bold text-lg" data-testid="profile-name">{user.name}</p>
          <p className="text-gray-500 text-sm" data-testid="profile-phone">{user.phone}</p>
          <div className="flex gap-1.5 mt-1.5 flex-wrap">
            {user.roles.map((r) => (
              <span key={r} className="text-[11px] font-semibold bg-gray-100 px-2 py-0.5 rounded-full" data-testid={`role-badge-${r}`}>
                {ru.roles[r] || r}
              </span>
            ))}
          </div>
        </div>
      </div>

      {isAdmin && (
        <Link to="/admin" data-testid="admin-panel-link" className="flex items-center gap-3 bg-black text-white rounded-2xl p-4 mb-6 active:scale-[0.98] transition-transform">
          <Shield size={20} />
          <span className="font-medium">{ru.admin.title}</span>
        </Link>
      )}

      <div className="mb-6 space-y-3">
        <PushToggle />
        <RingSoundToggle />
        <InstallEntry />
      </div>

      <div className="space-y-3 mb-6">
        <label className="text-sm font-semibold text-gray-600">{ru.auth.name}</label>
        <input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        {isDriver && (
          <>
            <p className="text-sm font-semibold text-gray-600 pt-2">{ru.profile.driverSection}</p>
            <input data-testid="car-model-input" value={form.car_model} onChange={(e) => setForm({ ...form, car_model: e.target.value })} placeholder={ru.profile.carModel} className={inputCls} />
            <input data-testid="car-color-input" value={form.car_color} onChange={(e) => setForm({ ...form, car_color: e.target.value })} placeholder={ru.profile.carColor} className={inputCls} />
            <input data-testid="car-plate-input" value={form.car_plate} onChange={(e) => setForm({ ...form, car_plate: e.target.value })} placeholder={ru.profile.carPlate} className={inputCls} />
          </>
        )}
        <button data-testid="profile-save-button" onClick={save} disabled={saving} className="w-full h-12 bg-black text-white rounded-2xl font-medium active:scale-[0.98] transition-transform disabled:opacity-50">
          {ru.profile.save}
        </button>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{ru.invites.title}</h2>
          <button data-testid="create-invite-button" onClick={createInvite} className="flex items-center gap-1.5 text-sm font-semibold bg-gray-100 px-3 py-2 rounded-full active:scale-[0.95] transition-transform">
            <Plus size={16} /> {ru.invites.create}
          </button>
        </div>
        {invites.length === 0 && <p className="text-gray-400 text-sm" data-testid="invites-empty">{ru.invites.empty}</p>}
        <div className="space-y-2">
          {invites.map((inv) => (
            <div key={inv.id} data-testid={`invite-row-${inv.code}`} className="bg-gray-50 rounded-xl p-3.5 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-mono text-sm font-semibold truncate">{inv.code}</p>
                <p className={`text-xs ${inv.status === "used" ? "text-gray-400" : "text-[#34C759]"}`}>
                  {inv.status === "used" ? ru.invites.used : ru.invites.unused}
                  {inv.max_uses > 1 && ` · ${inv.used_count}/${inv.max_uses}`}
                  {inv.uses.length > 0 && ` · ${ru.invites.usedBy} ${inv.uses.map((u) => u.name).join(", ")}`}
                </p>
              </div>
              <button data-testid={`copy-invite-${inv.code}`} onClick={() => copyLink(inv.link)} className="p-2 rounded-full bg-white shrink-0 active:scale-[0.9] transition-transform">
                <Copy size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
